import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Pagination,
  Box,
  Grid,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  CircularProgress,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  keyframes,
} from "@mui/material";
import { Edit, Delete, Add, Visibility, Close } from "@mui/icons-material";
import NavigationTab from "../navigation";
import { Users } from "./userinterface";
import AddUserDialog from "./adduser";
import EditUserDialog from "./updateuser";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Styled components
const StyledTableCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  color: "white",
}));

const VehiclePlate = styled(Chip)(({ theme }) => ({
  margin: "2px",
  backgroundColor: "#2e7d32",
  color: "white",
  fontSize: "0.75rem",
}));

const AnimatedDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-container": {
    animation: `${fadeIn} 0.3s ease-out`,
  },
  "& .MuiPaper-root": {
    animation: `${slideIn} 0.3s ease-out`,
    background: "linear-gradient(145deg, #1a1a1a, #2d2d2d)",
    borderRadius: "12px",
    border: "1px solid #444",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    minWidth: "500px",
    maxWidth: "600px",
    overflow: "hidden",
  },
}));

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  background: "linear-gradient(to right, #2e7d32, #1b5e20)",
  color: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  padding: "24px",
  backgroundColor: "#1e1e1e",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#2a2a2a",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#2e7d32",
    borderRadius: "4px",
  },
}));

const VehicleListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: "#2a2a2a",
  marginBottom: "8px",
  borderRadius: "6px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#333",
    transform: "translateX(5px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": {
      borderColor: "#444",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#2e7d32",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2e7d32",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#aaa",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "white",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    transform: "rotate(90deg)",
  },
}));

const UserListTable: React.FC = () => {
  const [users, setUsers] = useState<Users[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Users | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<{
    id: number;
    status: boolean;
  } | null>(null);
  const [vehiclesDialogOpen, setVehiclesDialogOpen] = useState(false);
  const [currentVehicles, setCurrentVehicles] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVehicleUser, setSelectedVehicleUser] = useState<Users | null>(
    null
  );

  const fetchUsers = (currentPage: number) => {
    setLoading(true);
    setError(null);

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/user?page=${currentPage}&pageSize=${pageSize}`
    )
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch users");
        return response.json();
      })
      .then((data) => {
        const sortedUsers = (data.items || []).sort(
          (a: Users, b: Users) => a.id - b.id
        );
        setUsers(sortedUsers);
        setTotalCount(data.totalCount || 0);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        setError(error.message || "Failed to load users");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page]);

  const handleUserAdded = () => fetchUsers(page);
  const handleUserUpdated = () => fetchUsers(page);

  const handleEditUser = (user: Users) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleStatusChangeConfirm = () => {
    if (!userToToggle) return;

    setLoading(true);
    fetch(
      `https://kineticgreen-backend.navigolabs.com/user/userStatusChangeWTHT/${userToToggle.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: !userToToggle.status }),
      }
    )
      .then((response) => {
        if (!response.ok) throw new Error("Failed to update user status");
        fetchUsers(page);
      })
      .catch((error) => {
        console.error("Error updating status:", error);
        setError(error.message || "Failed to update user status");
      })
      .finally(() => setStatusDialogOpen(false));
  };

  const handleStatusChangeRequest = (
    userId: number,
    currentStatus: boolean
  ) => {
    setUserToToggle({ id: userId, status: currentStatus });
    setStatusDialogOpen(true);
  };

  const handleShowVehicles = (vehicles: any[], user: Users) => {
    setCurrentVehicles(vehicles);
    setSelectedVehicleUser(user);
    setVehiclesDialogOpen(true);
    setSearchTerm("");
  };

  const filteredVehicles = currentVehicles.filter(
    (vehicle) =>
      vehicle.plateNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.vin?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ background: "#1a1a1a", paddingBottom: 8 }}>
      <NavigationTab />
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "transparent",
          }}
        >
          <Box
            sx={{
              width: "1200px",
              background: "black",
              border: "1px solid white",
              borderRadius: "18px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
              }}
            >
              <Typography variant="h6" sx={{ color: "white" }}>
                User List
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  borderRadius: "20px",
                  background: "black",
                  color: "white",
                  border: "2px green solid",
                }}
                onClick={() => setDialogOpen(true)}
              >
                Add User
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "green" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Active/Inactive
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      User Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Role
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Vehicles
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <StyledTableCell colSpan={6} align="center">
                        <CircularProgress sx={{ color: "green" }} />
                      </StyledTableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={6}
                        align="center"
                        sx={{ color: "red", py: 3 }}
                      >
                        {error}
                        <Button
                          variant="outlined"
                          onClick={() => fetchUsers(page)}
                          sx={{ ml: 2, color: "white", borderColor: "green" }}
                        >
                          Retry
                        </Button>
                      </StyledTableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <StyledTableCell
                        colSpan={6}
                        align="center"
                        sx={{ color: "white", py: 3 }}
                      >
                        No users found
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <StyledTableCell>
                          <Switch
                            checked={user.status}
                            onChange={() =>
                              handleStatusChangeRequest(user.id, user.status)
                            }
                            color="primary"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{ bgcolor: "white", color: "black", mr: 2 }}
                            />
                            <Box>
                              <Typography sx={{ color: "white" }}>
                                {user.name}
                              </Typography>
                              <Typography
                                sx={{ color: "lightgray", fontSize: "0.8rem" }}
                              >
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: "white" }}>
                          {user.role?.name || "N/A"}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={user.status ? "Active" : "Inactive"}
                            sx={{
                              backgroundColor: user.status
                                ? "#4caf50"
                                : "#f44336",
                              color: "white",
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ color: "white", mr: 1 }}>
                              {user.vehicles?.length || 0} vehicles
                            </Typography>
                            {user.vehicles?.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleShowVehicles(user.vehicles, user)
                                }
                                sx={{ color: "white" }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <IconButton onClick={() => handleEditUser(user)}>
                            <Edit sx={{ color: "white" }} />
                          </IconButton>
                          <IconButton>
                            <Delete sx={{ color: "white" }} />
                          </IconButton>
                        </StyledTableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && !error && users.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "white",
                      backgroundColor: "green",
                      "&.Mui-selected": {
                        backgroundColor: "black",
                        color: "white",
                      },
                      "&:hover": {
                        backgroundColor: "darkgreen",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {userToToggle?.status ? "deactivate" : "activate"} this user?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusChangeConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Vehicles Dialog */}
      <AnimatedDialog
        open={vehiclesDialogOpen}
        onClose={() => setVehiclesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Vehicle Details
            </Typography>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              {selectedVehicleUser?.name || "User"} • ID:{" "}
              {selectedVehicleUser?.id}
            </Typography>
          </Box>
          <CloseButton onClick={() => setVehiclesDialogOpen(false)}>
            <Close />
          </CloseButton>
        </DialogHeader>
        <DialogContentStyled>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: "#aaa" }}>
                Registered Vehicles
              </Typography>
              <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                {currentVehicles.length} vehicle
                {currentVehicles.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Chip
              label={
                selectedVehicleUser?.status ? "Active User" : "Inactive User"
              }
              size="small"
              sx={{
                backgroundColor: selectedVehicleUser?.status
                  ? "#4caf50"
                  : "#f44336",
                color: "white",
              }}
            />
          </Box>

          <SearchField
            fullWidth
            variant="outlined"
            placeholder="Search by plate or VIN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ color: "#666", mr: 1 }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21 21L16.65 16.65"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              ),
            }}
          />

          <List sx={{ maxHeight: "400px", overflow: "auto", pr: 1 }}>
            {filteredVehicles.length > 0 ? (
              filteredVehicles.map((vehicle, index) => (
                <VehicleListItem key={index}>
                  <ListItemText
                    primary={vehicle.plateNo || "No plate number"}
                    primaryTypographyProps={{
                      color: "white",
                      fontWeight: "medium",
                      fontSize: "1rem",
                    }}
                    secondary={
                      <>
                        <span>VIN: {vehicle.vin || "N/A"}</span>
                        <br />
                        <span>
                          Status: {vehicle.status ? "Active" : "Inactive"}
                        </span>
                      </>
                    }
                    secondaryTypographyProps={{
                      color: "#aaa",
                      fontSize: "0.8rem",
                    }}
                  />
                </VehicleListItem>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  color: "#666",
                  textAlign: "center",
                  p: 3,
                }}
              >
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 3H21V21H3V3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 9H15V15H9V9Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography
                  variant="body1"
                  sx={{ mt: 2, fontWeight: "medium" }}
                >
                  No vehicles found
                </Typography>
                {searchTerm ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    No matches for "{searchTerm}"
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    This user has no vehicles registered
                  </Typography>
                )}
              </Box>
            )}
          </List>
        </DialogContentStyled>
        <DialogActions
          sx={{
            backgroundColor: "#1e1e1e",
            padding: "16px 24px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Button
            onClick={() => setVehiclesDialogOpen(false)}
            sx={{
              color: "white",
              backgroundColor: "#2e7d32",
              "&:hover": {
                backgroundColor: "#1b5e20",
              },
              borderRadius: "6px",
              padding: "8px 16px",
              textTransform: "none",
              minWidth: "100px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </AnimatedDialog>

      <AddUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onUserAdded={handleUserAdded}
      />

      <EditUserDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
        vehicles={selectedUser?.vehicles || []}
      />
    </Box>
  );
};

export default UserListTable;
